import {
  documentId,
  getDocs,
  limit,
  query,
  serverTimestamp,
  setDoc,
  where,
} from "firebase/firestore"
import { facilitatorDoc, facilitatorsCollection, toFacilitator } from "@/lib/firebase/firestore"
import type { AuthUser } from "@/lib/firebase/auth"
import type { Facilitator } from "@/types"

/*
 * Facilitator profiles.
 *
 * WHO MAY CALL WHAT — enforced by the Firestore rules, not by this layer.
 *
 *   Your own profile only: rememberFacilitator
 *   Any signed-in facilitator: getFacilitators
 *
 * The profile is a projection of the Google account, not a record anyone edits.
 * It exists so a customer shared with someone can be shown as a NAME: sharing
 * stores uids, and a uid renders as nothing.
 */

/**
 * Writes the signed-in account's profile, on every sign-in.
 *
 * A blind merge rather than a create-if-missing read: the write is idempotent,
 * the values come from the identity provider and are always the freshest ones
 * available, so skipping the read makes sign-in one operation instead of two.
 */
export async function rememberFacilitator(user: AuthUser): Promise<void> {
  await setDoc(
    facilitatorDoc(user.uid),
    {
      // Lower-cased on the way in so it can be looked up by equality:
      // `findFacilitatorByEmail` has no other key to search on, and Firestore
      // has no case-insensitive comparison.
      email: user.email.toLowerCase(),
      displayName: user.displayName,
      photoUrl: user.photoUrl,
      lastSeenAt: serverTimestamp(),
    },
    { merge: true }
  )
}

/**
 * Finds a colleague by their work address, for sharing a customer with them.
 *
 * They must have signed in at least once: the profile is written on sign-in, and
 * there is nothing else to look a person up by. An invitation to someone who
 * never has would be a uid nobody holds, silently doing nothing — better to say
 * so than to accept it.
 */
export async function findFacilitatorByEmail(email: string): Promise<Facilitator | null> {
  const wanted = email.trim().toLowerCase()
  if (wanted === "") return null

  const snapshot = await getDocs(
    query(facilitatorsCollection(), where("email", "==", wanted), limit(1))
  )
  const first = snapshot.docs[0]
  return first ? toFacilitator(first) : null
}

/**
 * Profiles for a set of uids — the facilitators a customer is shared with.
 *
 * Chunked at ten because that is Firestore's ceiling for an `in` query. A
 * customer shared with more than ten people is not a case worth a different
 * shape, but it must not silently return the first ten either.
 */
export async function getFacilitators(uids: string[]): Promise<Facilitator[]> {
  const unique = [...new Set(uids)].filter((uid) => uid !== "")
  if (unique.length === 0) return []

  const chunks: string[][] = []
  for (let i = 0; i < unique.length; i += 10) chunks.push(unique.slice(i, i + 10))

  const results = await Promise.all(
    chunks.map((chunk) =>
      getDocs(query(facilitatorsCollection(), where(documentId(), "in", chunk)))
    )
  )

  return results
    .flatMap((snapshot) => snapshot.docs)
    .map(toFacilitator)
    .filter((entry): entry is Facilitator => entry !== null)
}
